import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, Btn, Spinner, Alert, EmptyState, Modal } from '../../components/UI';
import { api } from '../../utils/api';

// Community → Chat: 1:1 messages between Basecamp members.
// Ported from HB Compass Messages — same polling model (no WebSocket).
const CONVERSATIONS_POLL_MS = 15000;
const THREAD_POLL_MS = 8000;

function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString([], { day: '2-digit', month: 'short' }) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function Avatar({ id, name, size = 36 }) {
  const [url, setUrl] = useState(null);
  useEffect(() => { let on = true; if (id) api.getMemberPhotoURL(id).then(u => { if (on) setUrl(u); }); return () => { on = false; }; }, [id]);
  const box = { width: size, height: size, borderRadius: '50%', flexShrink: 0, objectFit: 'cover', background: 'var(--ink)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.36, fontWeight: 600 };
  if (url) return <img src={url} alt="" style={box} />;
  const initials = (name || '?').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
  return <div style={box}>{initials || '?'}</div>;
}

function NewConversationModal({ open, onClose, people, onPick }) {
  const [q, setQ] = useState('');
  useEffect(() => { if (open) setQ(''); }, [open]);
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return people.filter(p => !t || `${p.name} ${p.email || ''} ${p.country || ''}`.toLowerCase().includes(t));
  }, [people, q]);
  return (
    <Modal open={open} onClose={onClose} title="New message">
      <input autoFocus placeholder="Search people…" value={q} onChange={e => setQ(e.target.value)}
        style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px', border: '1px solid var(--line)', borderRadius: 'var(--radius-md)', fontSize: '0.9rem', marginBottom: 14, fontFamily: 'var(--font-body)' }} />
      <div style={{ maxHeight: 340, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
        {filtered.length === 0 && <div style={{ fontSize: '0.85rem', color: 'var(--ink-soft)', padding: '12px 4px' }}>No one found.</div>}
        {filtered.map(p => (
          <div key={p.id} onClick={() => onPick(p)}
            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 12px', borderRadius: 'var(--radius-md)', cursor: 'pointer' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--canvas-warm)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
            <Avatar id={p.id} name={p.name} size={32} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '0.88rem', fontWeight: 500 }}>{p.name}</div>
              <div style={{ fontSize: '0.76rem', color: 'var(--ink-soft)' }}>{[p.country, p.email].filter(Boolean).join(' · ')}</div>
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}

export default function ChatPage() {
  const [params, setParams] = useSearchParams();
  const [conversations, setConversations] = useState(null);
  const [people, setPeople] = useState([]);
  const [activeId, setActiveId] = useState(() => { const v = parseInt(params.get('with'), 10); return Number.isInteger(v) ? v : null; });
  const [thread, setThread] = useState(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState(null);
  const endRef = useRef(null);

  const loadConversations = useCallback(() => api.chat.conversations().then(setConversations).catch(e => setError(e.message)), []);
  const loadThread = useCallback(async (otherId) => {
    try {
      setThread(await api.chat.thread(otherId));
      await api.chat.markRead(otherId);
      loadConversations();
    } catch (e) { setError(e.message); }
  }, [loadConversations]);

  useEffect(() => {
    loadConversations();
    api.chat.people().then(setPeople).catch(() => { });
    const t = setInterval(loadConversations, CONVERSATIONS_POLL_MS);
    return () => clearInterval(t);
  }, [loadConversations]);

  useEffect(() => {
    if (activeId == null) { setThread(null); return; }
    setThread(null);
    loadThread(activeId);
    const t = setInterval(() => loadThread(activeId), THREAD_POLL_MS);
    return () => clearInterval(t);
  }, [activeId, loadThread]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [thread]);

  function open(id) {
    setActiveId(id); setShowNew(false); setDraft('');
    setParams(id ? { with: String(id) } : {}, { replace: true });
  }

  async function send() {
    const body = draft.trim();
    if (!body || activeId == null || sending) return;
    setSending(true); setError(null);
    try { await api.chat.send(activeId, body); setDraft(''); await loadThread(activeId); }
    catch (e) { setError(e.message); }
    finally { setSending(false); }
  }

  const activeName = (conversations || []).find(c => c.otherId === activeId)?.name
    || people.find(p => p.id === activeId)?.name || '';

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ fontSize: '0.86rem', color: 'var(--ink-soft)' }}>Direct messages with anyone in the Hansen Beck community.</div>
        <Btn onClick={() => setShowNew(true)}>+ New message</Btn>
      </div>
      {error && <div style={{ marginBottom: 12 }}><Alert type="error">{error}</Alert></div>}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'stretch' }}>
        {/* Conversations */}
        <Card style={{ flex: '1 1 260px', minWidth: 0, padding: 8, overflowY: 'auto', maxHeight: 600, minHeight: 420 }}>
          {conversations === null ? <div style={{ padding: 30, textAlign: 'center' }}><Spinner /></div>
            : conversations.length === 0 ? <div style={{ padding: 16 }}><EmptyState icon="💬" title="No conversations yet" message="Start one with “+ New message”." /></div>
              : conversations.map(c => (
                <div key={c.otherId} onClick={() => open(c.otherId)} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: 11, borderRadius: 'var(--radius-md)', cursor: 'pointer',
                  background: activeId === c.otherId ? 'var(--canvas-warm)' : 'transparent',
                }}>
                  <Avatar id={c.otherId} name={c.name} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ fontSize: '0.88rem', fontWeight: c.unread > 0 ? 700 : 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</span>
                      <span style={{ fontSize: '0.7rem', color: 'var(--ink-soft)', flexShrink: 0 }}>{formatWhen(c.lastMessageAt)}</span>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: c.unread > 0 ? 'var(--ink)' : 'var(--ink-soft)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.lastMessage}</div>
                  </div>
                  {c.unread > 0 && <div style={{ minWidth: 20, height: 20, borderRadius: 10, background: 'var(--accent)', color: '#fff', fontSize: '0.72rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 6px', flexShrink: 0 }}>{c.unread}</div>}
                </div>
              ))}
        </Card>

        {/* Thread */}
        <Card style={{ flex: '2 1 380px', minWidth: 0, padding: 0, display: 'flex', flexDirection: 'column', maxHeight: 600, minHeight: 420 }}>
          {activeId == null ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <EmptyState icon="✉️" title="Pick a conversation" message="…or start a new one." />
            </div>
          ) : (
            <>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--line)', fontWeight: 600, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: 10 }}>
                <Avatar id={activeId} name={activeName} size={30} /> {activeName}
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {thread === null ? <div style={{ textAlign: 'center', padding: 30 }}><Spinner /></div>
                  : thread.length === 0 ? <div style={{ margin: 'auto', color: 'var(--ink-faint)', fontSize: '0.85rem' }}>Say hello 👋</div>
                    : thread.map(m => (
                      <div key={m.id} style={{ display: 'flex', justifyContent: m.mine ? 'flex-end' : 'flex-start' }}>
                        <div style={{
                          maxWidth: '72%', padding: '9px 13px', borderRadius: 'var(--radius-md)',
                          background: m.mine ? 'var(--ink)' : 'var(--canvas-warm)', color: m.mine ? '#fff' : 'var(--ink)',
                          fontSize: '0.88rem', lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        }}>
                          {m.body}
                          <div style={{ fontSize: '0.66rem', opacity: 0.65, marginTop: 4, textAlign: 'right' }}>{formatWhen(m.at)}</div>
                        </div>
                      </div>
                    ))}
                <div ref={endRef} />
              </div>
              <div style={{ padding: '12px 18px', borderTop: '1px solid var(--line)', display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                <textarea value={draft} onChange={e => setDraft(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Write a message… (Enter to send, Shift+Enter for a new line)" rows={2} maxLength={5000}
                  style={{ flex: 1, resize: 'none', padding: '10px 14px', border: '1px solid var(--line)', borderRadius: 'var(--radius-md)', fontSize: '0.88rem', fontFamily: 'var(--font-body)' }} />
                <Btn onClick={send} loading={sending} disabled={!draft.trim()}>Send</Btn>
              </div>
            </>
          )}
        </Card>
      </div>

      <NewConversationModal open={showNew} onClose={() => setShowNew(false)} people={people} onPick={p => open(p.id)} />
    </>
  );
}
